/**
 * NIGHTBOUND - Adventure (locked).
 *
 * The system does not exist yet, but its *data* does: the wild creature roster
 * is already defined in the shared future module and the asset folders are in
 * place. This screen is honest about that - it shows the shape of what is
 * coming without pretending the door opens.
 */

import { useGame } from '../state/GameProvider'
import { WILD_LIST, type WildCreatureDefinition } from '../../shared/future/wild'
import { Link } from 'react-router-dom'
import { Chip, Panel, PanelHeading } from '../components/ui'

const ROADMAP = [
  { title: 'Expeditions', detail: 'Send your creature out into the dark for a bounded time.' },
  { title: 'Turn-based combat', detail: 'Attack, defend, and use the abilities it has learned.' },
  { title: 'Devouring', detail: 'Eating what you kill restores hunger at a cost to sanity.' },
  { title: 'Essence & mutations', detail: 'Absorb a species to change your creature permanently.' },
]

export function AdventureScreen() {
  const { catalog } = useGame()
  const wild = (catalog?.wildCreatures as WildCreatureDefinition[] | undefined) ?? WILD_LIST

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
      <header className="mb-5">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-2xl text-bone-100">Adventure</h1>
          <Chip tone="warn">Locked</Chip>
        </div>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ash-400">
          The stairwell goes down further than the shelter. Beyond it there are things with teeth
          that have never been fed by anyone. Your creature is not ready, and neither is this
          milestone.
        </p>
      </header>

      <div className="grid gap-5 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-4">
          <Panel className="overflow-hidden">
            <PanelHeading right={<Chip tone="neutral">preview</Chip>}>
              Known wild creatures
            </PanelHeading>
            <ul className="divide-y divide-ash-700/30">
              {wild.map((creature) => (
                <li key={creature.key} className="flex items-start gap-3 px-4 py-3 opacity-80">
                  <span
                    aria-hidden
                    className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[3px] border border-ash-700/40 bg-night-950/60 text-xl"
                  >
                    {creature.family === 'Canine'
                      ? '🐺'
                      : creature.family === 'Feline'
                        ? '🐆'
                        : creature.family === 'Avian'
                          ? '🐦‍⬛'
                          : '🐍'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-bone-200">{creature.name}</span>
                      <Chip tone="neutral">tier {creature.tier}</Chip>
                      {creature.signatureEffect ? (
                        <Chip tone="bad">{creature.signatureEffect.toLowerCase()}</Chip>
                      ) : null}
                    </div>
                    <p className="mt-0.5 text-[11px] leading-snug text-ash-400">
                      {creature.description}
                    </p>
                    <p className="mt-1 flex flex-wrap gap-1">
                      {creature.habitats.map((habitat) => (
                        <Chip key={habitat} tone="neutral">
                          {habitat}
                        </Chip>
                      ))}
                    </p>
                  </div>
                  <div className="hidden shrink-0 grid-cols-4 gap-2 text-center sm:grid">
                    <MiniStat label="HP" value={creature.baseStats.hp} />
                    <MiniStat label="ATK" value={creature.baseStats.attack} />
                    <MiniStat label="DEF" value={creature.baseStats.defence} />
                    <MiniStat label="SPD" value={creature.baseStats.speed} />
                  </div>
                </li>
              ))}
            </ul>
          </Panel>
        </div>

        <aside className="space-y-4">
          <Panel className="overflow-hidden">
            <PanelHeading>Not yet built</PanelHeading>
            <ul className="space-y-3 p-4">
              {ROADMAP.map((item) => (
                <li key={item.title} className="flex gap-3">
                  <span aria-hidden className="mt-1 text-ash-500">
                    ◆
                  </span>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.12em] text-bone-300">
                      {item.title}
                    </div>
                    <p className="mt-0.5 text-[11px] leading-snug text-ash-400">{item.detail}</p>
                  </div>
                </li>
              ))}
            </ul>
          </Panel>
          <Link
            to="/shelter"
            className="inline-flex w-full items-center justify-center rounded-[3px] border border-ash-600 bg-night-700/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-bone-300 transition-colors hover:bg-night-600"
          >
            Back to the shelter
          </Link>
        </aside>
      </div>
    </div>
  )
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-[3px] border border-ash-700/40 bg-night-950/50 px-1.5 py-1">
      <div className="nb-numeric text-xs text-bone-300">{value}</div>
      <div className="nb-label text-[7px]">{label}</div>
    </div>
  )
}
